import { useState } from 'react';
import {
  List,
  Datagrid,
  ListProps,
  TextField,
  useUpdate,
  useNotify,
  useRefresh,
  TextInput,
  useRecordContext,
} from 'react-admin';

import { Answer } from '../domain.js';
import { DefaultPagination } from '../components/pagination.js';

const postFilters = [
  <TextInput label="Question" source="question_id" alwaysOn />
];

export const CheckList: React.FC<ListProps> = (props) => (
  <List
    {...props}
    filters={postFilters}
    perPage={100}
    pagination={<DefaultPagination />}
    exporter={false}
    sort={{ field: 'created_at', order: 'DESC' }}
  >
    <Datagrid>
      <TextField source="question_id" />
      <TranscriptField source="transcript" />
      <CheckBlock />
      <TextField source="checked_at" />
      <TextField source="tags" />
    </Datagrid>
  </List>
);

// Helper function to clean the transcript
const cleanTranscript = (transcript: string) => {
  // Remove text inside square brackets and replace "/n" with newlines
  return transcript
    .replace(/\[.*?\]\ \ \ /g, '') // Removes text inside square brackets
    .replace(/\/n/g, '\n');  // Replace "/n" with newline characters
};

// TranscriptField component to display cleaned transcript with newlines
const TranscriptField: React.FC<{ source: string }> = ({ source }) => {
  const record = useRecordContext<Answer>();
  if (!record) return null;

  const notify = useNotify();
  const [update] = useUpdate();

  // Handle save button
  const handleSave = () => {
    const tagArray = (record.tags ? record.tags.split(',') : [])
      .filter(str => !str.startsWith('r_')); // return filtered tag array with no "r_"
  
    update(
      'answer',
      { 
        id: record.id,
        data: {
          tags: tagArray.toString(),
          checked_at: 'now',
        },
      },
      {
        onSuccess: () => notify('Tags updated', { type: 'success' }),
        onError: (error) => notify('Error updating tags', { type: 'error' }),
      }
    );
  };

  // Clean and prepare the transcript for display
  const cleanedTranscript = cleanTranscript(source === 'transcript' ? record.transcript || '' : '');

  return (
    <pre style={{ whiteSpace: 'pre-wrap' }} onClick={handleSave}>{cleanedTranscript}</pre>
  );
};

const CheckBlock: React.FC = () => {
  const record = useRecordContext<Answer>();
  if (!record) return null;
  
  const notify = useNotify();
  const [update, { isLoading }] = useUpdate();

  // Populate tagArray from tags 
  const tagArray: Array<string> = record.tags ? record.tags.split(',') : [];
 
  // Handle save button
  const handleSave = (r_type: string) => {
    const newTags = [...tagArray];

    switch(r_type) {
      case 'none':
        // Filter out all redaction tags
        newTags.length = 0;
        break;
      
      case 'name':
        if (!newTags.includes('r_name')) {
          newTags.push('r_name');
        }
        break;
        
      case 'location':
        if (!newTags.includes('r_location')) {
          newTags.push('r_location');
        }
        break;

      case 'other':
        if (!newTags.includes('r_other')) {
          newTags.push('r_other');
        }
        break;
    }
  
    update(
      'answer',
      { 
        id: record.id,
        data: {
          tags: newTags.join(','),
          checked_at: 'now',
        },
        previousData: record,
      },
      {
        onSuccess: () => notify('Tags updated', { type: 'success' }),
        onError: (error) => notify('Error updating tags', { type: 'error' }),
      }
    );
  };
  
  if (record.transcript) {
    return (
      <>
        <button disabled={isLoading} onClick={() => handleSave('none')}>None</button><br/><br/>
        <button disabled={isLoading} onClick={() => handleSave('name')}>Name</button>
        <button disabled={isLoading} onClick={() => handleSave('location')}>Location</button>
        <button disabled={isLoading} onClick={() => handleSave('other')}>Other</button>
      </>
    ); 
  } else {
    return (
      <p>No transcript available</p>
    );
  }
};

